import { useTranslation } from "react-i18next";
import { AudioLines, FileText, Globe, Languages, Lock } from "lucide-react";

import { languageLabelKey } from "../../../../shared/i18n/contentLanguages";
import {
    Meditation,
    MeditationKind,
    formatDuration,
} from "../../domain/types";
import CopyCount from "./CopyCount";
import MeditationActionsMenu, { type MeditationMenuItem } from "./MeditationActionsMenu";
import MeditationLikeButton from "./MeditationLikeButton";

type Props = {
    meditation: Meditation;
    /** Opens it: the whole entry is the way in. */
    onOpen: () => void;
    /** What stays in view beside it: adding someone else's, for instance. */
    action?: React.ReactNode;
    /** The rest of what can be done with it, behind "⋯". */
    menu?: MeditationMenuItem[];
    /** Shown under everything else, the width of the whole entry. */
    footer?: React.ReactNode;
};

const KIND_ICON: Record<MeditationKind, typeof FileText> = {
    [MeditationKind.TEXT]: FileText,
    [MeditationKind.AUDIO]: AudioLines,
    [MeditationKind.TEXT_AND_AUDIO]: AudioLines,
};

/**
 * One meditation in a library, as a line of a list.
 *
 * The card's answer to "what is it, how long, whose" in one row, for a
 * therapist who knows roughly what they are after and wants to run their eye
 * down titles rather than across a wall of cards. The tags stay on the card:
 * here the filter bar does that job.
 */
export default function MeditationRow({ meditation, onOpen, action, menu = [], footer }: Props) {
    const { t } = useTranslation();
    const KindIcon = KIND_ICON[meditation.kind];
    const isPublic = meditation.visibility === "public";

    return (
        <article className="relative flex flex-col gap-3 has-[[aria-haspopup=menu][aria-expanded=true]]:z-20 rounded-xl border border-border-subtle bg-surface-raised px-4 py-3 shadow-theme-xs sm:flex-row sm:flex-wrap sm:items-center sm:gap-4 transition-shadow hover:shadow-theme-md">
            <div className="flex min-w-0 flex-1 items-center gap-3">
                <span
                    className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-sunken text-content-secondary"
                    title={t(`meditation.kinds.${meditation.kind}`)}
                >
                    <KindIcon size={16} aria-hidden="true" />
                    <span className="sr-only">{t(`meditation.kinds.${meditation.kind}`)}</span>
                </span>
                <div className="min-w-0">
                    <h3 className="truncate text-sm font-semibold text-content-primary">
                        <button
                            type="button"
                            onClick={onOpen}
                            className="rounded-sm text-left after:absolute after:inset-0 after:rounded-xl after:content-[''] hover:text-accent-text focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-accent"
                        >
                            {meditation.title}
                        </button>
                    </h3>
                    <p className="flex min-w-0 items-center gap-1.5 text-xs text-content-tertiary">
                        {meditation.author_photo_url && (
                            <img
                                src={meditation.author_photo_url}
                                alt=""
                                loading="lazy"
                                className="size-4 shrink-0 rounded-full object-cover"
                            />
                        )}
                        <span className="truncate">
                            {meditation.author_name
                                ? t("meditation.byAuthor", { author: meditation.author_name })
                                : meditation.description}
                        </span>
                    </p>
                </div>
            </div>

            <dl className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 text-xs text-content-tertiary">
                <div className="flex items-center gap-1">
                    <dt className="sr-only">{t("meditation.duration")}</dt>
                    <dd className="font-medium text-content-secondary">
                        {formatDuration(
                            meditation.declared_duration_seconds || meditation.estimated_seconds
                        )}
                    </dd>
                </div>
                {meditation.language && (
                    <div className="flex items-center gap-1">
                        <dt className="sr-only">{t("meditation.fields.language")}</dt>
                        <dd className="flex items-center gap-1">
                            <Languages size={12} aria-hidden="true" />
                            {t(languageLabelKey(meditation.language), meditation.language)}
                        </dd>
                    </div>
                )}
                <div className="flex items-center gap-1">
                    <dt className="sr-only">{t("meditation.visibilityLabel")}</dt>
                    <dd className="flex items-center gap-1">
                        {isPublic ? (
                            <Globe size={12} aria-hidden="true" />
                        ) : (
                            <Lock size={12} aria-hidden="true" />
                        )}
                        {t(isPublic ? "meditation.visibility.public" : "meditation.visibility.private")}
                    </dd>
                </div>
            </dl>

            <div className="relative z-10 flex shrink-0 items-center justify-end gap-2">
                <MeditationLikeButton meditation={meditation} />
                <CopyCount count={meditation.copy_count} />
                {action}
                <MeditationActionsMenu title={meditation.title} items={menu} />
            </div>
            {footer && <div className="relative z-10 basis-full">{footer}</div>}
        </article>
    );
}
